import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Assuming you might want links later
import './LandingPage.css'; // We'll create this CSS file next
import { motion } from 'framer-motion';
import ClientTrustSection from '../components/landing/ClientTrustSection';
import VideoDemoModal from '../components/landing/VideoDemoModal';
import { trackTikTokEvent, trackFacebookEvent } from '../utils/tracking';
import { 
  ArrowRight, 
  Play, 
  Check, 
  DollarSign, 
  Megaphone, 
  BarChart3, 
  Users,
  Plug,
  Sliders,
  Rocket,
  Building2,
  TrendingUp,
  Shield,
  MessageCircle
} from 'lucide-react';

const WHATSAPP_SALES_URL = 'https://wa.me/201001795335?text=%5BWeb_Sales%5D%20Hi%20Promotly%2C%20I%27m%20interested%20in%20Promotly%20for%20my%20business.';

const LandingPage = () => {
  // Example realm - replace with dynamic logic if needed
  const defaultRealm = 'promotly-test-realm';
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');

  // TikTok Pixel: ViewContent on landing page load
  useEffect(() => {
    trackTikTokEvent('ViewContent', { content_type: 'landing_page', content_name: 'Homepage' });
    trackFacebookEvent('ViewContent', { content_type: 'landing_page', content_name: 'Homepage' });
  }, []);

  return (
    <div className="landing-page">
      {/* Override global admin layout padding that leaks to public pages */}
      <style>{`main.main-content.flex-grow { padding: 20px 0 !important; overflow-x: visible !important; }`}</style>
      {/* Hero Section — Asymmetric Two-Column Split (55/45) */}
      <section className="bg-gradient-to-br from-teal-50/80 via-white to-teal-50/40 overflow-hidden pt-10 pb-6 md:pt-14 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 w-full items-center">
          
          {/* Left Column — Copy (Enterprise Path) */}
          <div className="text-left flex flex-col lg:justify-between lg:h-full">
            <div>
              {/* Eyebrow Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-800 text-sm font-medium mb-6">
                🏬 Multi-Vendor Loyalty Built for Middle East Malls &amp; Retail Groups
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[52px] font-bold text-gray-900 leading-[1.08] tracking-tight">
                The Only Loyalty Platform Where Every Vendor Owns Their Customers
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 mt-6 max-w-2xl leading-relaxed">
                Multi-vendor loyalty platform purpose-built for Middle East retail groups, malls, and multi-branch brands. <strong className="text-teal-600">Seller-scoped points</strong>, real-time fraud detection, and native Arabic RTL — trusted by <strong className="text-teal-600">500+</strong> businesses.
              </p>
            </div>
            
            {/* Enterprise CTA */}
            <div className="mt-8">
              <div className="bg-white/60 rounded-xl p-4 border border-teal-100">
                <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">For Enterprise &amp; Malls</p>
                <a
                  href="https://cal.com/ahmed-abdulazzim/promotly-demo"
                  className="w-full bg-teal-600 hover:bg-teal-700 active:bg-teal-800 active:scale-[0.98] text-white font-semibold px-6 py-3 rounded-lg text-base inline-flex items-center justify-center gap-2 transform transition-all duration-150 no-underline"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={WHATSAPP_SALES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full mt-2 bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-[0.98] text-white font-semibold px-6 py-3 rounded-lg text-base inline-flex items-center justify-center gap-2 transform transition-all duration-150 no-underline"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat with Sales on WhatsApp
                </a>
              </div>
              <p className="mt-2 text-sm text-gray-500 font-medium">Typically 30 mins walkthrough</p>
            </div>
          </div>

          {/* Right Column — Video + SMB Path */}
          <div className="flex flex-col gap-4">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-gray-900 cursor-pointer group" style={{ aspectRatio: '16 / 9' }} onClick={() => setIsVideoModalOpen(true)}>
              {/* Static thumbnail placeholder — uses the poster image from VideoDemoModal */}
              <img 
                src="/assets/images/landing/video-thumbnail.jpg" 
                alt="Promotly demo video preview" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105"
              />
              
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
              
              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[68px] h-[68px] rounded-full bg-white shadow-lg flex items-center justify-center transition-transform duration-300 ease-in-out group-hover:scale-110">
                  <div className="w-0 h-0 ml-1 border-t-[14px] border-t-transparent border-l-[22px] border-l-teal-600 border-b-[14px] border-b-transparent" />
                </div>
              </div>

              {/* Duration Badge */}
              <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs sm:text-sm font-medium pointer-events-none">
                0:30
              </div>
            </div>

            {/* Video CTA */}
            <button onClick={() => setIsVideoModalOpen(true)} className="self-start inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium bg-white hover:bg-slate-50 hover:border-slate-400 transition-colors duration-150">
              <Play className="w-4 h-4 text-slate-500" />
              See How It Works (30s)
            </button>

            {/* SMB CTA */}
            <div>
              <div className="bg-white/60 rounded-xl p-4 border border-teal-100">
                <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">For Single Brands &amp; SMBs</p>
                <Link 
                  to="/new-tenant-signup"
                  className="w-full bg-white hover:bg-gray-50 active:scale-[0.98] text-teal-700 font-semibold px-6 py-3 rounded-lg text-base inline-flex items-center justify-center gap-2 border-2 border-teal-600 transform transition-all duration-150 no-underline"
                >
                  Start Free Trial
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="mt-2 text-sm text-gray-500 font-medium">No credit card required • 14-day free trial</p>
            </div>
          </div>

        </div>
        </div>
      </section>

      {/* How It Works Section — 3-Step Visual */}
      <section className="pt-6 pb-10 md:pt-8 md:pb-14 bg-gradient-to-b from-gray-50/80 via-white to-gray-50/60 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-800 text-sm font-medium mb-5">
              How It Works
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              Live in Under 10 Minutes
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              From signup to your first loyalty campaign — fast, simple, and built for Middle East retail.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative">
            {/* Connecting Line (desktop only) */}
            <div className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-teal-200 via-teal-400 to-teal-200" style={{ width: 'calc(100% - 8rem)', marginLeft: '4rem' }} />

            {/* Step 1 */}
            <motion.div 
              className="relative flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-teal-600 text-white shadow-lg mb-6">
                <Plug className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Connect</h3>
              <p className="text-gray-600 leading-relaxed max-w-xs">
                Integrate with your POS system, e-commerce platform, or CRM in minutes. No technical skills needed.
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div 
              className="relative flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-teal-600 text-white shadow-lg mb-6">
                <Sliders className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Configure</h3>
              <p className="text-gray-600 leading-relaxed max-w-xs">
                Set loyalty tiers, earning rules, and rewards that match your business model perfectly.
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div 
              className="relative flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-teal-600 text-white shadow-lg mb-6">
                <Rocket className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Engage</h3>
              <p className="text-gray-600 leading-relaxed max-w-xs">
                Launch automated campaigns, track performance, and watch customer retention grow.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <ClientTrustSection />

      {/* Trust Banner — Compressed Stats Strip */}
      <section className="py-8 bg-gradient-to-r from-teal-50 via-white to-teal-50 border-y border-teal-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center divide-y sm:divide-y-0 sm:divide-x divide-teal-100">
            <div className="flex-1 text-center py-3 px-6">
              <div className="text-2xl sm:text-3xl font-extrabold text-teal-600" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>380%</div>
              <div className="text-xs sm:text-sm text-gray-500 font-medium uppercase tracking-wider">Average ROI Across Active Accounts</div>
            </div>
            <div className="flex-1 text-center py-3 px-6">
              <div className="text-2xl sm:text-3xl font-extrabold text-gray-900" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>95%</div>
              <div className="text-xs sm:text-sm text-gray-500 font-medium uppercase tracking-wider">Client Satisfaction Score (Last 12 Months)</div>
            </div>
            <div className="flex-1 text-center py-3 px-6">
              <div className="text-2xl sm:text-3xl font-extrabold text-teal-600" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>40%</div>
              <div className="text-xs sm:text-sm text-gray-500 font-medium uppercase tracking-wider">Improvement in Customer Retention, Retail Clients</div>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">Metrics reflect aggregated performance data from Promotly clients across retail and F&amp;B sectors.</p>
        </div>
      </section>

      {/* Global Compliance Section (Phase 3.1) */}
      <section className="py-14 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Enterprise-Grade Trust, Built In</h2>
            <p className="text-gray-500 mt-2 text-sm">Security, compliance, and reliability your team can depend on.</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">Data Residency &amp; Local Compliance</div>
              <div className="text-xs text-gray-500">Data hosted in-region with full compliance to regional data protection laws.</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">GDPR Readiness</div>
              <div className="text-xs text-gray-500">Built-in data subject rights, consent management, and breach notification workflows.</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">Enterprise Encryption (AES-256)</div>
              <div className="text-xs text-gray-500">All data encrypted at rest and in transit with industry-standard AES-256 and TLS 1.3.</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">99.9% Uptime SLA</div>
              <div className="text-xs text-gray-500">High-availability infrastructure with guaranteed uptime for mission-critical operations.</div>
            </div>
          </div>

          {/* Integration Ecosystem Logo Strip (Phase 3.2) */}
          <div className="mt-12 pt-10 border-t border-gray-200">
            <p className="text-center text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">Native Ecosystem Integrations</p>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              <span className="text-lg font-bold text-gray-300 hover:text-gray-600 transition-colors">Foodics</span>
              <span className="text-lg font-bold text-gray-300 hover:text-gray-600 transition-colors">Salla</span>
              <span className="text-lg font-bold text-gray-300 hover:text-gray-600 transition-colors">Zid</span>
              <span className="text-lg font-bold text-gray-300 hover:text-gray-600 transition-colors">Zoho</span>
              <span className="text-lg font-bold text-gray-300 hover:text-gray-600 transition-colors">Shopify</span>
              <span className="text-lg font-bold text-gray-300 hover:text-gray-600 transition-colors">Magento</span>
            </div>
          </div>
        </div>
      </section>

      {/* Two Architectures Section — Dark Slate Background */}
      <motion.section 
        className="py-20 px-4 bg-slate-900 overflow-hidden relative"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Gradient bridge from previous section */}
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-white to-slate-900" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 text-teal-400 text-sm font-medium mb-4 border border-teal-500/20">
              TWO ARCHITECTURES. ONE PLATFORM.
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Choose the Model That Matches Your Exact Hierarchy</h2>
            <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
              Promotly is the only loyalty platform that supports both operating models natively — no custom development, no workarounds.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            
            {/* Left Card: Multi-Vendor Mode (white) */}
            <div className="relative bg-white rounded-2xl p-8 shadow-2xl border-2 border-teal-400">
              {/* Ribbon Badge */}
              <div className="absolute -top-3 right-6 px-4 py-1 rounded-full bg-teal-600 text-white text-xs font-bold shadow-lg">
                Most platforms can't do this
              </div>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Multi-Vendor Mode</h3>
                  <p className="text-sm text-gray-500">Each vendor/sub-seller gets independent points, wallets, and dashboards — all under one platform.</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-600" /></div>
                  <span><strong className="text-gray-900">Seller-scoped points</strong> — points stay with each vendor, no co-mingling</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-600" /></div>
                  <span><strong className="text-gray-900">Independent dashboards</strong> — each branch sees only its own data</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-600" /></div>
                  <span><strong className="text-gray-900">Blocked cross-redemption</strong> — prevent points from being spent at unauthorized branches</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-600" /></div>
                  <span><strong className="text-gray-900">HQ campaign automation</strong> — push targeted campaigns per vendor from one console</span>
                </li>
              </ul>
              <Link to="/features" className="text-teal-600 font-semibold text-sm inline-flex items-center gap-1 hover:underline">
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Right Card: Unified Brand Mode (dark-on-dark) */}
            <div className="relative bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-700">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Unified Brand Mode</h3>
                  <p className="text-sm text-slate-400">One unified loyalty pool across all touchpoints — branches, online, and mobile.</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-400" /></div>
                  <span><strong className="text-white">Shared points pool</strong> — earn anywhere in your brand, spend everywhere</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-400" /></div>
                  <span><strong className="text-white">Centralized campaigns</strong> — one brand message across every channel</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-400" /></div>
                  <span><strong className="text-white">Real-time analytics</strong> — unified view of member behavior across all locations</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-teal-400" /></div>
                  <span><strong className="text-white">Native integrations</strong> — POS, e-commerce, and mobile SDK included</span>
                </li>
              </ul>
              <Link to="/features" className="text-teal-400 font-semibold text-sm inline-flex items-center gap-1 hover:underline">
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

          </div>

          {/* Enterprise Feature Strip (Task 2.3) */}
          <div className="mt-14">
            <p className="text-center text-xs font-semibold text-teal-400/80 uppercase tracking-widest mb-6">Included in every plan</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              <div className="flex flex-col items-center justify-center bg-slate-800/60 rounded-xl px-3 py-5 border border-slate-700/50 text-center">
                <span className="text-2xl mb-2">🎁</span>
                <span className="text-sm font-medium text-slate-200 leading-tight">5 Reward Types</span>
              </div>
              <div className="flex flex-col items-center justify-center bg-slate-800/60 rounded-xl px-3 py-5 border border-slate-700/50 text-center">
                <span className="text-2xl mb-2">🛡️</span>
                <span className="text-sm font-medium text-slate-200 leading-tight">Real-Time Fraud Detection</span>
              </div>
              <div className="flex flex-col items-center justify-center bg-slate-800/60 rounded-xl px-3 py-5 border border-slate-700/50 text-center">
                <span className="text-2xl mb-2">⏰</span>
                <span className="text-sm font-medium text-slate-200 leading-tight">Auto Inactivity Engine</span>
              </div>
              <div className="flex flex-col items-center justify-center bg-slate-800/60 rounded-xl px-3 py-5 border border-slate-700/50 text-center">
                <span className="text-2xl mb-2">🌐</span>
                <span className="text-sm font-medium text-slate-200 leading-tight">Native Arabic RTL</span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Product Screenshots — See the Platform in Action */}
      <motion.section
        className="py-16 px-4 bg-white overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-800 text-sm font-medium mb-3">
              See the Platform in Action
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              Purpose-Built Dashboards for Every Role
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              From the control room to the checkout counter — each interface is designed for the person using it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mall Admin Dashboard */}
            <motion.div
              className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="relative overflow-hidden border-b border-gray-100">
                <img
                  src="/assets/images/landing/screenshot-admin-dashboard.jpg"
                  alt="Mall Admin KPI Dashboard showing real-time loyalty analytics"
                  className="w-full h-auto transition-transform duration-500 ease-in-out group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center mb-3">
                  <Building2 className="w-4 h-4 text-teal-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Mall Admin Dashboard</h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">Real-time KPI overview — member growth, campaign performance, and revenue impact across all sellers.</p>
              </div>
            </motion.div>

            {/* Fraud Detection Panel */}
            <motion.div
              className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative overflow-hidden border-b border-gray-100">
                <img
                  src="/assets/images/landing/screenshot-fraud-detection.jpg"
                  alt="Fraud Detection panel with rule-based monitoring"
                  className="w-full h-auto transition-transform duration-500 ease-in-out group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center mb-3">
                  <Shield className="w-4 h-4 text-teal-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Fraud Detection Engine</h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">Real-time rule-based monitoring — detect suspicious activity, auto-block fraud, and protect your points ecosystem.</p>
              </div>
            </motion.div>

            {/* Cashier Rewards Gateway */}
            <motion.div
              className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="relative overflow-hidden border-b border-gray-100">
                <img
                  src="/assets/images/landing/screenshot-cashier-gateway.jpg"
                  alt="Cashier Rewards Gateway for earn/redeem at point of sale"
                  className="w-full h-auto transition-transform duration-500 ease-in-out group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center mb-3">
                  <DollarSign className="w-4 h-4 text-teal-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Cashier Rewards Gateway</h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">One-click earn/redeem at the point of sale — look up customers, apply rewards, and close transactions in seconds.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <motion.section 
        className="bg-gradient-to-br from-teal-50 via-white to-teal-50 py-16 border-y border-teal-100"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Real Results. Real Businesses.</h2>
            <div className="w-12 h-1 bg-teal-500 rounded-full mt-3 mx-auto" />
            <p className="text-sm text-gray-500 mt-2">Across UAE, KSA, Egypt &amp; GCC</p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center divide-y md:divide-y-0 md:divide-x divide-teal-100 bg-white rounded-2xl shadow-xl border border-teal-100 max-w-3xl mx-auto overflow-hidden">
            <motion.div 
              className="flex-1 flex flex-col items-center justify-center py-10 px-8 text-center hover:bg-teal-50 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mb-3 mx-auto">
                <Building2 className="w-5 h-5 text-teal-600" />
              </div>
              <div className="text-4xl md:text-5xl font-extrabold text-teal-600 tracking-tight leading-none" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>
                500<span className="text-2xl font-bold text-teal-400 align-top ml-0.5">+</span>
              </div>
              <div className="text-sm font-medium text-gray-500 mt-2 uppercase tracking-widest">Active Businesses</div>
            </motion.div>

            <motion.div 
              className="flex-1 flex flex-col items-center justify-center py-10 px-8 text-center hover:bg-teal-50 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mb-3 mx-auto">
                <Users className="w-5 h-5 text-teal-600" />
              </div>
              <div className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-none" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>
                2M<span className="text-2xl font-bold text-gray-400 align-top ml-0.5">+</span>
              </div>
              <div className="text-sm font-medium text-gray-500 mt-2 uppercase tracking-widest">Loyalty Members</div>
            </motion.div>

            <motion.div 
              className="flex-1 flex flex-col items-center justify-center py-10 px-8 text-center hover:bg-teal-50 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mb-3 mx-auto">
                <TrendingUp className="w-5 h-5 text-teal-600" />
              </div>
              <div className="text-4xl md:text-5xl font-extrabold text-teal-600 tracking-tight leading-none" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>
                40<span className="text-2xl font-bold text-teal-400 align-top ml-0.5">%</span>
              </div>
              <div className="text-sm font-medium text-gray-500 mt-2 uppercase tracking-widest">Avg. Retention Increase</div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Final CTA Section — Split-Card Layout */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-600 to-teal-700">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Ready to Transform Your Customer Loyalty?</h2>
          <p className="text-teal-100 mb-10 max-w-xl mx-auto">Choose the path that fits your business model.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
            {/* Enterprise Card */}
            <div className="bg-slate-900 rounded-2xl p-8 shadow-xl text-left">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Enterprise &amp; Mall Operators</h3>
                  <p className="text-sm text-slate-400">Multi-vendor loyalty at scale.</p>
                </div>
              </div>
              <ul className="space-y-2.5 mb-7">
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" /> Seller-scoped points with zero cross-redemption</li>
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" /> Centralized campaign automation per branch</li>
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" /> Advanced analytics across all locations</li>
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" /> Dedicated account manager + priority support</li>
              </ul>
              <a href="https://cal.com/ahmed-abdulazzim/promotly-demo" className="w-full bg-teal-600 hover:bg-teal-500 text-white font-semibold px-5 py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2 transition-colors no-underline">
                Book a Demo <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <p className="text-xs text-slate-500 mt-2 text-center">Typically 30 mins walkthrough</p>
            </div>
            
            {/* SMB Card */}
            <div className="bg-white rounded-2xl p-8 shadow-xl text-left">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                  <Rocket className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">SMBs &amp; Single Brands</h3>
                  <p className="text-sm text-gray-500">Get started in minutes. No technical skills needed.</p>
                </div>
              </div>
              <ul className="space-y-2.5 mb-7">
                <li className="flex items-start gap-2.5 text-sm text-gray-600"><Check className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> Pre-built loyalty tiers &amp; reward templates</li>
                <li className="flex items-start gap-2.5 text-sm text-gray-600"><Check className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> POS &amp; e-commerce integrations (one-click)</li>
                <li className="flex items-start gap-2.5 text-sm text-gray-600"><Check className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> Real-time customer analytics dashboard</li>
                <li className="flex items-start gap-2.5 text-sm text-gray-600"><Check className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> Self-serve setup, no onboarding fee</li>
              </ul>
              <Link to="/new-tenant-signup" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2 transition-colors no-underline">
                Start Free Trial <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <a href={WHATSAPP_SALES_URL} target="_blank" rel="noopener noreferrer" className="w-full mt-2 border border-green-500 text-green-700 hover:bg-green-50 font-semibold px-5 py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2 transition-colors no-underline">
                <MessageCircle className="w-3.5 h-3.5" /> Chat with Sales on WhatsApp
              </a>
              <p className="text-xs text-gray-400 mt-2 text-center">No credit card required</p>
            </div>
          </div>
        </div>
      </section>
      {/* Newsletter Pre-Footer */}
      <section className="py-10 px-4 bg-teal-600">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Stay Updated</h3>
          <p className="text-sm text-teal-100 mb-5 max-w-md mx-auto">Get loyalty insights, product updates, and industry trends delivered to your inbox.</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email" 
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 border border-teal-400 text-white placeholder-teal-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent text-sm"
              aria-label="Email for newsletter"
            />
            <button
              onClick={() => {
                if (newsletterEmail) {
                  trackTikTokEvent('Subscribe', { email: newsletterEmail });
                  trackFacebookEvent('Lead', { value: 0, currency: 'USD' });
                }
              }}
              className="bg-white text-teal-700 hover:bg-teal-50 px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Subscribe
            </button>
          </div>
          <p className="text-xs text-teal-200 mt-3">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </section>
      {/* Parent Company Credibility Strip */}
      <section className="py-8 px-4 bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-gray-600 leading-relaxed">
            Built by <span className="font-semibold text-gray-900">Sharee Coin</span> — a Cairo-based loyalty infrastructure company since 2018, backed by Fahad Al Rajhi. 200+ merchants served · 8M EGP GMV · trusted across MENA &amp; GCC.
          </p>
          <Link to="/about" className="inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:text-teal-700 mt-2 transition-colors no-underline">
            Learn about the company <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
      <VideoDemoModal isOpen={isVideoModalOpen} onClose={() => setIsVideoModalOpen(false)} />
    </div>
  );
};

export default LandingPage;
